import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { X } from "lucide-react";
import { CvCategory, listCategories } from "features/cv-processor/api/CvCategoryApi";

interface Props {
  defaultTitle: string;
  onSave: (title: string, categoryId: number) => Promise<void>;
  onClose: () => void;
}

const saveAsNewCoverLetterSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  categoryId: Yup.number().required("Category is required"),
});

/** Small modal for saving a tailored cover letter (from CV Processor's own
 * enhancement flow or from a job card's "Enhanced Cover Letter" view) as a
 * brand-new regular cover letter. Category is required, mirroring
 * NewCoverLetterModal's validation — every cover letter must be tagged for
 * CV auto-pairing. */
export default function SaveAsNewCoverLetterModal({ defaultTitle, onSave, onClose }: Props) {
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const activeCategories = categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family);

  const formik = useFormik({
    initialValues: { title: defaultTitle, categoryId: "" as number | "" },
    validationSchema: saveAsNewCoverLetterSchema,
    onSubmit: async (vals, { setSubmitting }) => {
      setApiError("");
      try {
        await onSave(vals.title.trim(), Number(vals.categoryId));
      } catch (e: any) {
        setApiError(e?.response?.data?.detail ?? e?.message ?? "Could not save. Please try again.");
        setSubmitting(false);
      }
    },
  });

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-surface border border-border rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground font-inter">Save as new Cover Letter</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Cover letter title</label>
              <input
                type="text"
                {...formik.getFieldProps("title")}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
              {formik.touched.title && formik.errors.title && (
                <p className="mt-1 text-xs text-destructive font-inter">{formik.errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Category</label>
              <select
                name="categoryId"
                value={formik.values.categoryId}
                onChange={(e) => formik.setFieldValue("categoryId", e.target.value === "" ? "" : Number(e.target.value))}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="">— Select a category —</option>
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {formik.touched.categoryId && formik.errors.categoryId && (
                <p className="mt-1 text-xs text-destructive font-inter">{formik.errors.categoryId}</p>
              )}
            </div>

            {apiError && <p className="text-xs text-destructive font-inter">{apiError}</p>}
          </div>

          <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={formik.isSubmitting}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.values.title.trim() || formik.values.categoryId === ""}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              {formik.isSubmitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
